<template>
  <div>
    <v-data-table
      ref="cfsTableRef"
      :disable-sort="$vuetify.breakpoint.xs || disableSort"
      checkbox-color="primary"
      class="cfs-table"
      v-bind="{ ...$props, ...$attrs }"
      :item-class="rowClass"
      :headers="activeHeaders"
      :loading="loading"
      :page="currentPage"
      :mobile-breakpoint="layout === 'tablet' ? 9999 : 821"
      hide-default-footer
      :class="{
        'collapse-header': collapseHeader,
        'center-columns': centerColumns,
        'spaced-columns': spacedColumns,
        'tr-background': trBackground,
        rounded: rounded,
        transparent,
      }"
      v-on="$listeners"
    >
      <template v-if="showColumnsMenu" #header.actions>
        <v-menu v-model="columnCustomizationMenu" offset-y :close-on-content-click="false">
          <template #activator="{ on, attrs }">
            <CfsIcon
              class="cursor-pointer m-auto cfs-table__column-customization-icon"
              v-bind="attrs"
              icon-name="table-edit"
              :max-width="20"
              v-on="on"
            />
          </template>
          <v-card class="px-2" min-width="350px">
            <v-card-text class="p-1 pt-3">
              <v-text-field
                v-model.trim="searchText"
                class="px-1"
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                :placeholder="$t('SearchFields')"
                hide-details
              />
              <v-list>
                <v-virtual-scroll
                  height="300"
                  item-height="45"
                  :items="filterHeaderOptions(headerModel)"
                >
                  <template #default="{ item }">
                    <v-list-item class="px-2">
                      <CfsCheckbox
                        v-model="item.active"
                        :label="item.text"
                        :disabled="item.readonly"
                        hide-details
                        dense
                      />
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </v-list>
              <div class="flex justify-end items-center p-1">
                <v-btn text class="mx-2" color="title" @click="resetTableHeaders">
                  {{ $t('Reset') }}
                </v-btn>
                <v-btn color="primary" @click="applyHeaderConfiguration()">
                  {{ $t('Apply') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
      <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>

      <template v-if="$hasSlot('no-data')" #no-data>
        <slot name="no-data" />
      </template>
    </v-data-table>
    <PaginationComponent
      v-if="hasPagination"
      class="x-sm:!pb-[1.5rem]"
      :class="paginationClass"
      :page.sync="currentPage"
      :initial-current-page="currentPage"
      :total-items="totalItems"
      :items-per-page="itemsPerPage"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, PropType, watch } from 'vue';
import { useToggle } from '@/composables/useToggle';
import { CfsTableHeaders, useCfsTableStore } from '@/store/modules/components/useCfsTableStore';
import { DataTableHeader } from 'vuetify';
import { storeToRefs } from 'pinia';
import { useState } from '@/composables/common/useState';
import { jsonCopyObject } from '@/helpers/objects';
import { useDebouncedRef } from '@/composables/useDebouncedRef';
import { useLayout } from '@/composables/useLayout';

/** Key is the hashlike of concatenation of all values in the headers. Will be used to save it in the manage*/
const generateTableHash = (headers: DataTableHeader[] = []) => {
  const pseudoHash = [...headers.reduce((p, c) => (p += c.value), '')].reduce(
    (p, c) => (p += c.charCodeAt(0)),
    0
  );
  return `headershash$${pseudoHash}`;
};

export default defineComponent({
  components: {
    PaginationComponent: defineAsyncComponent(
      () => import('@/components/pagination/PaginationComponent.vue')
    ),
  },
  props: {
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    items: {
      type: Array as PropType<unknown[]>,
      default: () => [],
    },
    headers: {
      type: Array as PropType<DataTableHeader[]>,
      default: () => [],
    },
    centerColumns: {
      type: Boolean,
      default: false,
    },
    collapseHeader: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    spacedColumns: {
      type: Boolean,
      default: false,
    },
    /** if specified will be used instead of auto generated table key to manage table columns preferences. */
    tableName: {
      type: String,
      default: null,
    },
    paginationClass: {
      type: String,
      default: '',
    },
    rowClass: {
      type: Function,
      default: () => '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    trBackground: {
      type: Boolean,
      default: false,
    },
    hasPagination: {
      type: Boolean,
      default: false,
    },
    serverTotalItems: {
      type: Number,
      default: 0,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    serverSort: {
      type: Boolean,
      default: false,
    },
    headerFontSize: {
      type: String,
      default: '14px',
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    setActiveHeaders: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:page'],
  setup(props, { emit }) {
    const { layout } = useLayout();

    const currentPage = computed({
      get() {
        return props.page;
      },
      set(page) {
        emit('update:page', page);
      },
    });
    const [columnCustomizationMenu, toggleColumnCustomizationMenu] = useToggle();
    const searchText = useDebouncedRef<string>('', 300);

    const filterHeaderOptions = (headers: CfsTableHeaders[]): CfsTableHeaders[] => {
      return headers.filter(
        (h) => h.text.toLowerCase().includes(searchText.value.toLowerCase()) && !h?.showColumnsMenu
      );
    };

    const cfsTableStore = useCfsTableStore();

    const { headers: storeHeaders } = storeToRefs(cfsTableStore);

    const headersKey = computed(() => {
      return props.tableName ? props.tableName : generateTableHash(props.headers);
    });

    const [headerModel, setHeaderModel] = useState<CfsTableHeaders[]>([]);

    const computedHeaders = computed<
      (DataTableHeader & { active: boolean; showColumnsMenu?: boolean })[]
    >(() => {
      return props.headers?.map((header) => ({
        ...header,
        ...(header.value === 'actions' || header.value === 'index' ? { sortable: false } : {}),
        ...(props.serverSort ? { sort: () => 0 } : {}),
      }));
    });

    const showColumnsMenu = computed<boolean>(() => {
      return !!computedHeaders.value?.find((h) => h?.showColumnsMenu);
    });

    const activeHeaders = computed(() => {
      let headers = storeHeaders.value[headersKey.value]?.filter((h) => h.active);

      // Filter out columns that should be hidden on tablet
      if (layout.value === 'tablet') {
        headers = headers?.filter((h) => !h.hideOnMobile);
      }

      return headers;
    });

    watch(
      computedHeaders,
      (headers) => {
        /**Set provided headers anyway. They will be merged to ensure cached data is up to latest changes */
        cfsTableStore.initTableHeaders({
          key: headersKey.value,
          value: headers.map((h) => {
            return {
              ...h,
              //default values
              ...(props.setActiveHeaders
                ? {
                    active: true,
                    selected: true,
                  }
                : { active: h.active, selected: h.active }),
            };
          }),
        });
        setHeaderModel(jsonCopyObject(storeHeaders.value[headersKey.value]));
      },
      { immediate: true }
    );

    const applyHeaderConfiguration = () => {
      toggleColumnCustomizationMenu(false);
      cfsTableStore.toggleTableHeaders({
        key: headersKey.value,
        value: headerModel.value.map((h) => {
          return {
            ...h,
          };
        }),
      });
    };
    const resetTableHeaders = () => {
      toggleColumnCustomizationMenu(false);
      cfsTableStore.toggleTableHeaders({
        key: headersKey.value,
        value: computedHeaders.value,
      });
      setHeaderModel(jsonCopyObject(computedHeaders.value));
    };

    const totalItems = computed(() => {
      return props.serverTotalItems || props.items?.length;
    });

    return {
      layout,
      totalItems,
      currentPage,
      columnCustomizationMenu,
      searchText,
      resetTableHeaders,
      applyHeaderConfiguration,
      toggleColumnCustomizationMenu,
      showColumnsMenu,
      activeHeaders,
      storeHeaders,
      headerModel,
      headersKey,
      filterHeaderOptions,
    };
  },
});
</script>

<style lang="scss">
%sort-icon {
  background: url('@/assets/img/icons/arrow/chevron-filled-bottom.svg');
  @apply ml-[3px] w-[9px] h-[5px] opacity-100 content-[''];
}

.cfs-table.collapse-header {
  .v-data-table-header {
    visibility: collapse !important;
  }
}

.cfs-table {
  .v-data-table-header {
    .sortable {
      $activeSortIconColor: invert(38%) sepia(3%) saturate(12%) hue-rotate(314deg) brightness(94%)
        contrast(90%);

      .v-data-table-header__icon {
        @apply rotate-0;
      }

      .v-icon {
        @apply inline-block opacity-100;

        &::before {
          @extend %sort-icon;
          @apply mt-[1px];
        }

        &::after {
          @extend %sort-icon;
          @apply mt-[2px] rotate-180;
        }
      }

      &.active {
        &.asc .v-icon {
          &::after {
            @apply contrast-0;
          }
        }

        &.desc .v-icon {
          &::before {
            @apply contrast-0;
          }
        }
      }
    }

    &-mobile {
      &__wrapper {
        @apply w-[48%] ml-auto;
      }
    }
  }

  thead {
    tr {
      th:last-child:not(th:first-child, th:last-child):not(.fc-col-header-cell):not(
          .fc-timeline-slot
        ) {
        @apply shadow-none md:border-solid md:border-b-[thin] md:border-b-[#0000001f] #{!important};
      }
    }
    th:first-child {
      border-top-left-radius: 4px;
    }
    th:last-child {
      border-top-right-radius: 4px;
    }
  }

  table {
    border-collapse: collapse;
  }

  tbody {
    @apply tablet-p:grid tablet-p:grid-cols-2 #{!important};
    .v-data-table__empty-wrapper {
      @apply flex items-center justify-center sm:table-row tablet-p:pb-[2.5rem];
      td {
        @apply h-auto sm:h-[48px] #{!important};
      }
    }

    tr {
      &:is(.bg-salomie):hover {
        background: #fcdb8833 !important;
      }

      &:is(.bg-graer):hover {
        background: rgba(27, 38, 59, 0.06) !important;
      }

      &:is(.v-data-table__expanded__content) {
        box-shadow: none !important;
        padding: 0;
        margin: 0;
      }

      @apply relative m-[1rem_0.75rem] md:pb-0 pb-[1rem] pt-[2.5rem] md:pt-0 shadow-[0_3px_10px_rgb(0_0_0/19%)] md:m-0 md:shadow-none rounded-[10px] md:rounded-none;
      &.v-data-table__empty-wrapper {
        @apply col-span-full;
        td {
          @apply tablet-p:contents;
        }
      }

      & td {
        //&:nth-child(1) {
        //  @apply md:mt-0 mt-4;
        //}
        @apply min-h-[2rem] md:min-h-[40px] md:whitespace-nowrap lg:whitespace-normal #{!important};
        &:last-child:not(td:first-child) {
          @apply right-0 top-[2px] absolute w-full md:w-[auto] md:static border-b-[0px] md:border-b-[thin] md:border-b-[#0000001f];
        }

        .v-data-table__mobile-row__cell {
          @apply text-ellipsis overflow-hidden;
        }
      }
    }
  }

  tr {
    .text-start {
      padding: 0 0 0 10px !important;
    }
  }

  color: var(--v-text-base) !important;

  .v-data-table-header tr th span {
    font-weight: 500;
    color: var(--v-gray-base);
  }

  tbody tr td {
    font-weight: 500;
    font-size: 12px;
    line-height: 16.34px;
  }

  thead tr th {
    z-index: 3 !important;
    text-wrap: none;
    white-space: nowrap;
    font-size: v-bind(headerFontSize) !important;
  }

  &.rounded {
    .v-data-table__wrapper {
      @apply rounded-md;
    }
  }

  &.transparent {
    thead {
      tr {
        th {
          border-bottom: none !important;
        }
      }
    }

    tbody {
      tr {
        td {
          @apply border-none #{!important};
        }
      }
    }
  }

  &.tr-background {
    thead {
      tr {
        th {
          border-bottom: none !important;
        }
      }
    }

    tbody {
      tr {
        &:nth-child(odd) {
          background-color: rgba(27, 38, 59, 0.04) !important;
        }

        td {
          @apply border-none #{!important};
        }
      }
    }
  }

  &.center-columns {
    th,
    td {
      text-align: center !important;
    }
  }

  &.spaced-columns {
    th,
    td {
      text-align: center !important;
    }

    th:first-child,
    td:first-child {
      text-align: start !important;
    }

    th:last-child,
    td:last-child {
      text-align: end !important;
    }
  }

  .v-simple-checkbox {
    .theme--light.v-icon.v-icon.v-icon--disabled {
      color: rgba(0, 0, 0, 0.15) !important;
    }
  }
}
</style>
